import { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap'
import PageHeader from '../../../../layout/page/PageHeader'
import useAxiosPrivate from '../../../../../hooks/useAxiosPrivate'
import { useToast } from '../../../../common/toast/hooks/useToast'
import dayjs from 'dayjs'
import { Subscription } from './Subscriptions.d'

const View = () => {
    const { subscriptionId } = useParams()
    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()
    const { triggerToast } = useToast()

    const [subscription, setSubscription] = useState<Subscription | null>(null)
    const [status, setStatus] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [errMsg, setErrMsg] = useState('')

    const loadSubscription = useCallback(async () => {
        setErrMsg('')
        setIsLoading(true)

        try {
            const response = await axiosPrivate
                .get(`/subscriptions/${subscriptionId}`)
                .finally(() => setIsLoading(false))

            if (response.data?.success) {
                setSubscription(response.data.data.subscription)
                setStatus(response.data.data.subscription.status)
            } else {
                setErrMsg('Failed to load subscription')
            }
        } catch (err) {
            setErrMsg('Failed to load subscription')
        }
    }, [axiosPrivate, subscriptionId])

    useEffect(() => {
        loadSubscription()
    }, [loadSubscription])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSaving(true)
        setErrMsg('')

        try {
            const response = await axiosPrivate.post(
                `/subscriptions/${subscriptionId}`,
                { status }
            )

            if (response.data?.success) {
                triggerToast({
                    severity: 'success',
                    summary: 'Updated!',
                    detail: response.data.data.msg,
                    life: 5000,
                })
                navigate('/people/clients/subscription-requests')
            } else {
                setErrMsg('Failed to update subscription')
            }
        } catch (err) {
            setErrMsg('Failed to update subscription')
        } finally {
            setIsSaving(false)
        }
    }

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    return (
        <>
            <PageHeader
                icon="user"
                subtitle="View and update subscription status"
                gradient="linear-gradient(135deg, #8b5cf6, #7c3aed)"
                header="Subscription Details"
            />
            {errMsg && (
                <Alert variant="danger">{errMsg}</Alert>
            )}
            {subscription && (
                <Card>
                    <Card.Body>
                        <Form onSubmit={handleSubmit}>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={subscription.email}
                                            disabled
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            value={status}
                                            onChange={(e) =>
                                                setStatus(e.target.value)
                                            }
                                        >
                                            <option value="active">
                                                Active
                                            </option>
                                            <option value="disabled">
                                                Disabled
                                            </option>
                                            <option value="banned">
                                                Banned
                                            </option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Source</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={subscription.source || 'N/A'}
                                            disabled
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Subscribed At</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={
                                                subscription.created_at
                                                    ? dayjs(
                                                          subscription.created_at
                                                      ).format(
                                                          'DD-MMM-YYYY @ HH:mm:ss'
                                                      )
                                                    : 'N/A'
                                            }
                                            disabled
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            {subscription.unsubscribed_at && (
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Unsubscribed At</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={dayjs(subscription.unsubscribed_at).format('DD-MMM-YYYY @ HH:mm:ss')}
                                                disabled
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Unsubscribe Reason</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                value={subscription.unsubscribe_reason || 'No reason provided'}
                                                disabled
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            )}
                            <div className="d-flex gap-2 justify-content-end mt-4">
                                <Button
                                    variant="secondary"
                                    onClick={() =>
                                        navigate(
                                            '/people/clients/subscription-requests'
                                        )
                                    }
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={isSaving}
                                >
                                    {isSaving
                                        ? 'Saving...'
                                        : 'Update Status'}
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            )}
        </>
    )
}

export default View
